import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { authAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, CheckCircle, Mail, RefreshCw } from "lucide-react";
import { BrandLogoFull, BrandFull } from "../components/BrandLogo";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const PREVIEW_MODE = process.env.REACT_APP_PREVIEW_MODE === "true";
const SKIP_CAPTCHA = process.env.REACT_APP_SKIP_CAPTCHA === "true" || PREVIEW_MODE;

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const { requestAccess } = useAuth();

  // Load Turnstile script and render widget
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || SKIP_CAPTCHA) return;

    const renderWidget = () => {
      if (turnstileRef.current && window.turnstile && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "dark",
          callback: (token) => setCaptchaToken(token),
          "expired-callback": () => setCaptchaToken(""),
          "error-callback": () => setCaptchaToken(""),
        });
      }
    };

    // Check if script already loaded
    if (window.turnstile) {
      renderWidget();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = renderWidget;
    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) { /* Widget may already be removed */ }
        widgetIdRef.current = null;
      }
    };
  }, []);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken("");
    if (widgetIdRef.current !== null && window.turnstile) {
      try { window.turnstile.reset(widgetIdRef.current); } catch (_) { /* Widget may not be ready */ }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!SKIP_CAPTCHA && !captchaToken) {
      toast.error("Please complete the CAPTCHA verification");
      return;
    }
    
    setLoading(true);
    try {
      const result = await requestAccess(name, email, password, captchaToken || "preview", refCode);
      setSubmitted(true);
      toast.success(result.message);
    } catch (error) {
      const message = error.response?.data?.detail || "Request failed";
      toast.error(message);
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const [resending, setResending] = useState(false);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const res = await authAPI.resendVerification(email);
      toast.success(res.data.message || "Verification email resent!");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to resend email";
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="relative z-10 w-full max-w-md animate-in">
          <div className="flex justify-center mb-8">
            <BrandLogoFull className="h-24" />
          </div>
          
          <Card className="glass-card">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-heading text-2xl font-bold tracking-tight uppercase text-foreground mb-3" data-testid="verify-email-heading">
                Verify Your Email
              </h2>
              <p className="text-muted-foreground mb-4" data-testid="verify-email-message">
                We've sent a verification link to <span className="text-primary font-semibold">{email}</span>
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Click the link in the email to verify your address and complete your registration.
              </p>
              <div className="p-4 bg-white/[0.03] rounded-xl mb-6">
                <p className="text-sm text-muted-foreground">
                  Didn't receive it? Check your <span className="text-primary font-semibold">spam folder</span> or click resend below.
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={handleResendVerification}
                  disabled={resending}
                  variant="outline"
                  className="w-full h-12 font-semibold tracking-wide uppercase rounded-xl border-primary/50 text-primary hover:bg-primary/10"
                  data-testid="resend-verification-btn"
                >
                  {resending ? <div className="spinner" /> : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </Button>
                <Link to="/">
                  <Button
                    variant="ghost"
                    className="w-full h-12 font-semibold tracking-wide uppercase rounded-xl text-muted-foreground hover:text-foreground"
                    data-testid="back-to-login-btn"
                  >
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="relative z-10 w-full max-w-md animate-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <BrandFull iconSize="w-12 h-12" textSize="text-2xl" />
        </div>
        
        {/* Beta Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full">
            <CheckCircle className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Free Forever for Trainers</span>
          </div>
        </div>
        
        <Card className="glass-card">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="font-heading text-2xl tracking-tight uppercase font-extrabold">
              Create Your Free Account
            </CardTitle>
            <CardDescription className="text-muted-foreground font-bold text-base">
              Start managing your clients in 30 seconds
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Google Sign Up — Primary */}
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://demobackend.emergentagent.com/auth/v1/env/oauth?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 h-12 bg-white hover:bg-gray-50 text-gray-800 font-semibold rounded-xl transition-colors border border-gray-200 mb-4"
              data-testid="google-signup-btn"
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Sign up with Google
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-white/[0.06]" />
              <span className="text-xs text-muted-foreground/60 uppercase tracking-wider">or sign up with email</span>
              <div className="flex-1 h-px bg-white/[0.06]" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                  Full Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 bg-white/[0.03] border-white/[0.06] focus:border-brand-blue/50 rounded-xl transition-smooth"
                  data-testid="register-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="coach@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-white/[0.03] border-white/[0.06] focus:border-brand-blue/50 rounded-xl transition-smooth"
                  data-testid="register-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 bg-white/[0.03] border-white/[0.06] focus:border-brand-blue/50 rounded-xl transition-smooth pr-12"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-12 bg-white/[0.03] border-white/[0.06] focus:border-brand-blue/50 rounded-xl transition-smooth"
                  data-testid="register-confirm-password-input"
                />
              </div>

              {/* Cloudflare Turnstile CAPTCHA - hidden when skipped */}
              {!SKIP_CAPTCHA && (
                <div className="flex justify-center" data-testid="turnstile-container">
                  <div ref={turnstileRef} />
                </div>
              )}

              {PREVIEW_MODE && (
                <div className="text-center py-2 px-3 rounded-xl bg-[#00BFFF]/5 border border-[#00BFFF]/20" data-testid="preview-mode-badge">
                  <p className="text-xs text-[#00BFFF]">Preview Mode — Email verification disabled</p>
                </div>
              )}
              
              <Button
                type="submit"
                className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                disabled={loading || (!SKIP_CAPTCHA && !captchaToken)}
                data-testid="register-submit-btn"
              >
                {loading ? <div className="spinner" /> : "Create Account"}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link
                  to="/"
                  className="text-primary hover:underline font-medium"
                  data-testid="login-link"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Branding Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            Tracked with <span className="text-primary font-medium">FitTrack Coach</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <a href="https://app.fittrackcoach.in" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">app.fittrackcoach.in</a>
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">by InfiNest Tech Pvt. Ltd.</p>
        </div>
      </div>
    </div>
  );
}
